import Cookies from 'js-cookie'

import { poll } from '../poll'
import { wait } from '../wait'

let chatScriptLoaded = false

interface HubSpotEventConversation {
  conversationId?: string
}

interface HubSpotEventPayload {
  unreadCount?: number
  conversation?: HubSpotEventConversation
}

export interface HubSpotSettings {
  loadImmediately?: boolean
  identificationEmail?: string
  identificationToken?: string
}

enum HubSpotEvent {
  ConversationStarted = 'conversationStarted',
  ConversationClosed = 'conversationClosed',
  UserSelectedThread = 'userSelectedThread',
  UnreadConversationCountChanged = 'unreadConversationCountChanged',
  ContactAssociated = 'contactAssociated',
  UserInteractedWithWidget = 'userInteractedWithWidget',
  WidgetLoaded = 'widgetLoaded',
  QuickReplyButtonClick = 'quickReplyButtonClick',
  WidgetClosed = 'widgetClosed',
}

export interface HubSpotConversations {
  clear?: (input?: { resetWidget?: boolean }) => void
  on?: (event: HubSpotEvent, callback?: (eventPayload?: HubSpotEventPayload) => void) => void
  off?: (event: HubSpotEvent, callback?: (eventPayload?: HubSpotEventPayload) => void) => void
  widget?: {
    remove: () => void
    load: (input?: { widgetOpen?: boolean }) => void
    refresh?: (input?: { openToNewThread: boolean }) => void
    status?: () => { loaded: boolean }
    open?: () => void
    close?: () => void
  }
}

// Suppress HubSpot warnings in local dev
if (window.location.hostname === 'localhost') {
  const _warn = console.warn
  console.warn = (...args: any[]) => {
    if (
      typeof args[0] === 'string' &&
      args[0].startsWith('HubSpot Conversations: You are using conversations on a non-https site')
    ) {
      return
    }
    _warn.apply(console, args)
  }
}

const hubspotAppId = import.meta.env.VITE_HUBSPOT_APP_ID || '9294264'

const logger = (...args: any[]) => {
  const frame = new Error().stack?.split('\n')[2]?.trim()
  const functionName = frame?.includes('@')
    ? frame.split('@')[0] || 'anonymous' // Safari
    : frame?.split(' ')[1] || 'anonymous' // Chrome/Node

  const loggingIsEnabled = Cookies.get('hs-logging') === 'true'

  if (loggingIsEnabled) {
    console.log(`[HubSpot] [${functionName}]`, ...args)
  }
}

/**
 * Injects the HubSpot chat script into the page (if not already loaded).
 */
export function loadHubSpotChatScript(): Promise<void> {
  return new Promise((resolve) => {
    if (chatScriptLoaded || document.getElementById('hs-script-loader')) {
      resolve(undefined)
      return
    }
    chatScriptLoaded = true
    const script = document.createElement('script')
    script.id = 'hs-script-loader'
    script.src = `https://js.hs-scripts.com/${hubspotAppId}.js`
    script.async = true
    script.defer = true
    script.onload = () => {
      resolve(undefined)
    }
    document.body.appendChild(script)
  })
}

/**
 * Identifies a user in the HubSpot chat widget and shows the widget.
 * @param email  The user's email from auth context.
 * @param token  The secure identification token from account context.
 */
export async function handleAuthenticatedUser(email: string, token: string): Promise<void> {
  logger('Starting handleAuthenticatedUser')
  window.hsConversationsSettings = {
    ...(window.hsConversationsSettings || {}),
    identificationEmail: email,
    identificationToken: token,
    loadImmediately: false,
  }
  await poll(() => window.hsConversationsSettings?.identificationEmail === email)
  await loadHubSpotChatScript()
  logger('Authenticated user identified')
  await initializeHubSpotChat()
}

/**
 * Loads the HubSpot chat script and initializes the widget.
 */
export async function handleUnauthenticatedUser(): Promise<void> {
  logger('Starting handleUnauthenticatedUser')
  window.hsConversationsSettings = {
    ...(window.hsConversationsSettings || {}),
    identificationEmail: undefined,
    identificationToken: undefined,
    loadImmediately: false,
  }

  await poll(() => window.hsConversationsSettings?.identificationEmail !== undefined)
  await loadHubSpotChatScript()
  logger('Unauthenticated user identified')
  await initializeHubSpotChat()
}

/**
 * Initializes the HubSpot chat widget.
 */
export async function initializeHubSpotChat(): Promise<void> {
  logger('Starting initializeHubSpotChat')
  if (window.HubSpotConversations?.clear) {
    logger('Resetting and reloading widget')
    window.HubSpotConversations.clear?.({ resetWidget: true })
    await wait(250)
    logger('Loading widget after reset')
    await loadWidget()
  } else {
    logger('Loading widget')
    await loadWidget()
  }
}

/**
 * Open the chat widget.
 * If the Chatflow API exposes `open`, use it; otherwise fall back to load({widgetOpen:true}).
 */
export async function openHubSpotChat(): Promise<void> {
  logger('Starting openHubSpotChat')
  await waitForConversationsAPI()

  if (!isHubSpotLoaded()) {
    await loadWidget(true)
    return
  }

  const widget = window.HubSpotConversations?.widget
  if (widget?.open) {
    logger('Calling widget.open()')
    widget.open()
  } else {
    await loadWidget(true)
  }
}

/**
 * Close the chat widget.
 */
export async function closeHubSpotChat(): Promise<void> {
  logger('Starting closeHubSpotChat')
  const widget = window.HubSpotConversations?.widget

  if (!isHubSpotLoaded()) {
    await initializeHubSpotChat()
  }

  if (widget?.close) {
    logger('Calling widget.close()')
    widget.close()
  } else if (widget?.remove) {
    logger('Calling widget.remove()')
    widget.remove()
  } else {
    await loadWidget()
  }
}

/**
 * Returns whether the widget script has loaded.
 * (HubSpot's `status()` currently only tells you if it's loaded, not open.)
 */
const isHubSpotLoaded = (): boolean => {
  const status = window.HubSpotConversations?.widget?.status?.()
  logger('Checking if HubSpot is loaded', status)
  return !!status?.loaded
}

const waitForConversationsAPI = async (): Promise<void> => {
  await loadHubSpotChatScript()
  if (window.HubSpotConversations) return

  await new Promise<void>((resolve) => {
    window.hsConversationsOnReady = [...(window.hsConversationsOnReady || []), resolve]
  })
}

const loadWidget = async (widgetOpen = false): Promise<void> => {
  await waitForConversationsAPI()

  return new Promise((resolve) => {
    const conversations = window.HubSpotConversations
    const onLoaded = () => {
      conversations?.off?.(HubSpotEvent.WidgetLoaded, onLoaded)
      resolve()
    }

    conversations?.on?.(HubSpotEvent.WidgetLoaded, onLoaded)
    conversations?.widget?.load({ widgetOpen })
  })
}
